<template>
  <div class="onboarding-page">
    <div class="onboarding-container">

      <!-- Logo -->
      <div class="text-center mb-3">
        <b-img :src="appLogoImage" alt="Shireo" class="onboarding-logo" />
      </div>

      <!-- Step indicator -->
      <div class="wizard-steps d-flex justify-content-center align-items-center mb-3">
        <template v-for="(step, i) in steps">
          <div
            :key="`circle-${i}`"
            class="step-circle"
            :class="{
              'step-active': currentStep === i,
              'step-done': currentStep > i,
            }"
          >
            <feather-icon v-if="currentStep > i" icon="CheckIcon" size="14" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div
            v-if="i < steps.length - 1"
            :key="`line-${i}`"
            class="step-line"
            :class="{ 'step-line-done': currentStep > i }"
          />
        </template>
      </div>

      <!-- Step subtitle -->
      <p class="text-center text-muted mb-2" style="font-size: 0.85rem; letter-spacing: 0.05em; text-transform: uppercase; font-weight: 600;">
        Paso {{ currentStep + 1 }} de {{ steps.length }} — {{ steps[currentStep].title }}
      </p>

      <!-- ── STEP 1: Empresa ───────────────────────────────────── -->
      <b-card v-if="currentStep === 0" class="wizard-card">
        <h4 class="mb-25 font-weight-bold">Tu empresa</h4>
        <p class="text-muted mb-2">Cuéntanos un poco sobre tu empresa para configurar la cuenta demo.</p>
        <validation-observer ref="step1">
          <validation-provider #default="{ errors }" name="Nombre de empresa" rules="required">
            <b-form-group label="Nombre de la empresa" label-for="company-name">
              <b-form-input
                id="company-name"
                v-model="form.company.name"
                :state="errors.length ? false : null"
                placeholder="Mi Empresa S.L."
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="{ errors }" name="Comunidad autónoma" rules="required">
            <b-form-group label="Comunidad autónoma" label-for="autonomous-community">
              <b-form-select
                id="autonomous-community"
                v-model="form.company.autonomous_community"
                :state="errors.length ? false : null"
                :options="autonomousCommunityOptions"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </validation-observer>
      </b-card>

      <!-- ── STEP 2: Cuenta ───────────────────────────────────── -->
      <b-card v-if="currentStep === 1" class="wizard-card">
        <h4 class="mb-25 font-weight-bold">Tu cuenta de administrador</h4>
        <p class="text-muted mb-2">Serás el administrador de la cuenta demo durante 30 días.</p>
        <validation-observer ref="step2">
          <b-row>
            <b-col md="6">
              <validation-provider #default="{ errors }" name="Nombre" rules="required">
                <b-form-group label="Nombre" label-for="user-name">
                  <b-form-input
                    id="user-name"
                    v-model="form.user.name"
                    :state="errors.length ? false : null"
                    placeholder="Ana"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <validation-provider #default="{ errors }" name="Apellidos" rules="required">
                <b-form-group label="Apellidos" label-for="user-surname">
                  <b-form-input
                    id="user-surname"
                    v-model="form.user.surname"
                    :state="errors.length ? false : null"
                    placeholder="García López"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <validation-provider #default="{ errors }" name="Email" rules="required|email">
            <b-form-group label="Email" label-for="user-email">
              <b-form-input
                id="user-email"
                v-model="form.user.email"
                type="email"
                :state="errors.length ? false : null"
                placeholder="ana@empresa.com"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <b-row>
            <b-col md="6">
              <validation-provider #default="{ errors }" name="Contraseña" rules="required|min:8" vid="password">
                <b-form-group label="Contraseña" label-for="user-password">
                  <b-input-group class="input-group-merge" :class="errors.length ? 'is-invalid' : null">
                    <b-form-input
                      id="user-password"
                      v-model="form.user.password"
                      :type="showPassword ? 'text' : 'password'"
                      :state="errors.length ? false : null"
                      class="form-control-merge"
                      placeholder="Mínimo 8 caracteres"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                        class="cursor-pointer"
                        @click="showPassword = !showPassword"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <validation-provider #default="{ errors }" name="Confirmar contraseña" rules="required|confirmed:password">
                <b-form-group label="Confirmar contraseña" label-for="user-password-confirm">
                  <b-input-group class="input-group-merge" :class="errors.length ? 'is-invalid' : null">
                    <b-form-input
                      id="user-password-confirm"
                      v-model="form.user.passwordConfirm"
                      :type="showPasswordConfirm ? 'text' : 'password'"
                      :state="errors.length ? false : null"
                      class="form-control-merge"
                      placeholder="Repite la contraseña"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="showPasswordConfirm ? 'EyeOffIcon' : 'EyeIcon'"
                        class="cursor-pointer"
                        @click="showPasswordConfirm = !showPasswordConfirm"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </validation-observer>
      </b-card>

      <!-- ── STEP 3: Horario ──────────────────────────────────── -->
      <b-card v-if="currentStep === 2" class="wizard-card">
        <h4 class="mb-25 font-weight-bold">Configuración de horario</h4>
        <p class="text-muted mb-2">Define cómo trabaja tu empresa. Podrás ajustarlo después.</p>

        <!-- Has shifts toggle -->
        <div class="d-flex align-items-center mb-2 p-1 rounded" style="background:#f6f6f6;">
          <div class="flex-grow-1 mr-2">
            <div><strong>¿Tu empresa trabaja en turnos?</strong></div>
            <div class="text-muted" style="font-size:0.85rem;">Por ejemplo: turno de mañana, tarde y noche.</div>
          </div>
          <b-form-checkbox
            v-model="form.schedule.has_shifts"
            switch
            class="flex-shrink-0"
            style="min-width:0;"
          />
        </div>

        <!-- No shifts: simple schedule -->
        <div v-if="!form.schedule.has_shifts">
          <b-form-group label="Tipo de jornada" label-for="schedule-type" class="mb-2">
            <b-row>
              <b-col md="7">
                <div class="d-flex flex-column flex-md-row align-items-md-center" style="gap: 24px;">
                  <b-form-select
                    id="schedule-type"
                    v-model="form.schedule.type"
                    :options="[{ text: 'Continua', value: 'continua' }, { text: 'Partida', value: 'partida' }]"
                    size="sm"
                  />
                  <b-form-checkbox v-model="form.schedule.is_night_shift" class="mb-0 flex-shrink-0">
                    <small>Turno nocturno</small>
                  </b-form-checkbox>
                </div>
              </b-col>
            </b-row>
            <div class="text-muted mt-50" style="font-size:0.8rem;">
              <span v-if="form.schedule.type === 'continua'">Sin pausa para comer (o descanso corto).</span>
              <span v-else>Con pausa larga para comer (dos tramos horarios).</span>
            </div>
          </b-form-group>

          <div class="schedule-error text-danger mb-1" v-if="scheduleError">
            {{ scheduleError }}
          </div>

          <div class="preset-bar mb-1">
            <div class="d-flex align-items-center" style="gap:0.4rem;">
              <small class="preset-label mr-50">Rellenar:</small>
              <b-button
                v-for="p in dayPresets"
                :key="p.key"
                size="sm"
                variant="outline-secondary"
                pill
                class="preset-pill"
                @click="applyDayPreset(form.schedule.days, p.key)"
              >{{ p.label }}</b-button>
            </div>
            <b-button id="preset-info-main" variant="flat-secondary" size="sm" class="p-25 preset-info-btn">
              <feather-icon icon="InfoIcon" size="15" />
            </b-button>
            <b-popover target="preset-info-main" triggers="hover focus" placement="left">
              <template #title>Atajos de horario</template>
              Selecciona un atajo para rellenar rápidamente el rango de días. Las horas se toman del primer día activo y se aplican a todos los días del rango. Los días fuera del rango quedan desactivados automáticamente. Puedes editar cada día de forma independiente después.
            </b-popover>
          </div>

          <div class="table-responsive">
            <table class="table table-sm schedule-table">
              <thead>
                <tr>
                  <th>Día</th>
                  <th class="text-center">Activo</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th class="text-center">Descanso (min)</th>
                  <template v-if="form.schedule.type === 'partida'">
                    <th>Entrada 2</th>
                    <th>Salida 2</th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in form.schedule.days" :key="day.day" :class="{ 'day-row--off': !day.enabled }">
                  <td class="font-weight-bold" style="white-space:nowrap;">{{ day.label }}</td>
                  <td class="text-center">
                    <b-form-checkbox v-model="day.enabled" switch />
                  </td>
                  <td>
                    <small class="col-label">Entrada</small>
                    <div class="time-input-wrap">
                      <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <b-form-input v-model="day.start_time" type="time" :disabled="!day.enabled" :state="fieldError(day.day + '-start_time')" @focus="clearFieldError(day.day + '-start_time')" size="sm" />
                    </div>
                  </td>
                  <td>
                    <small class="col-label">Salida</small>
                    <div class="time-input-wrap">
                      <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <b-form-input v-model="day.end_time" type="time" :disabled="!day.enabled" :state="fieldError(day.day + '-end_time')" @focus="clearFieldError(day.day + '-end_time')" size="sm" />
                    </div>
                  </td>
                  <td class="text-center">
                    <small class="col-label">Descanso</small>
                    <div class="d-flex justify-content-center">
                      <b-form-input
                        v-model.number="day.rest_minutes"
                        type="number"
                        min="0"
                        :disabled="!day.enabled"
                        size="sm"
                        style="width:70px;"
                      />
                    </div>
                  </td>
                  <template v-if="form.schedule.type === 'partida'">
                    <td>
                      <small class="col-label">Entrada 2</small>
                      <div class="time-input-wrap">
                        <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <b-form-input v-model="day.start_time2" type="time" :disabled="!day.enabled" :state="fieldError(day.day + '-start_time2')" @focus="clearFieldError(day.day + '-start_time2')" size="sm" />
                      </div>
                    </td>
                    <td>
                      <small class="col-label">Salida 2</small>
                      <div class="time-input-wrap">
                        <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <b-form-input v-model="day.end_time2" type="time" :disabled="!day.enabled" :state="fieldError(day.day + '-end_time2')" @focus="clearFieldError(day.day + '-end_time2')" size="sm" />
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- With shifts -->
        <div v-else>
          <div class="schedule-error text-danger mb-1" v-if="scheduleError">
            {{ scheduleError }}
          </div>

          <div
            v-for="(shift, si) in form.schedule.shifts"
            :key="si"
            class="shift-block mb-3 p-2 border rounded"
          >
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0 font-weight-bold">Turno {{ si + 1 }}</h6>
              <b-button
                v-if="form.schedule.shifts.length > 1"
                variant="flat-danger"
                size="sm"
                class="btn-icon p-25"
                @click="removeShift(si)"
              >
                <feather-icon icon="TrashIcon" size="14" />
              </b-button>
            </div>

            <b-row class="mb-2">
              <b-col md="5">
                <b-form-group label="Nombre del turno" :label-for="`shift-name-${si}`" class="mb-1 mb-md-0">
                  <b-form-input
                    :id="`shift-name-${si}`"
                    v-model="shift.name"
                    placeholder="Ej: Turno Mañana"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
              <b-col md="7">
                <b-form-group label="Tipo de jornada" :label-for="`shift-type-${si}`" class="mb-0">
                  <div class="d-flex flex-column flex-md-row align-items-md-center" style="gap: 24px;">
                    <b-form-select
                      :id="`shift-type-${si}`"
                      v-model="shift.type"
                      :options="[{ text: 'Continua', value: 'continua' }, { text: 'Partida', value: 'partida' }]"
                      size="sm"
                    />
                    <b-form-checkbox v-model="shift.is_night_shift" class="mb-0 flex-shrink-0">
                      <small>Turno nocturno</small>
                    </b-form-checkbox>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="preset-bar mb-1">
              <div class="d-flex align-items-center" style="gap:0.4rem;">
                <small class="preset-label mr-50">Rellenar:</small>
                <b-button
                  v-for="p in dayPresets"
                  :key="p.key"
                  size="sm"
                  variant="outline-secondary"
                  pill
                  class="preset-pill"
                  @click="applyDayPreset(shift.days, p.key)"
                >{{ p.label }}</b-button>
              </div>
              <b-button :id="`preset-info-shift-${si}`" variant="flat-secondary" size="sm" class="p-25 preset-info-btn">
                <feather-icon icon="InfoIcon" size="15" />
              </b-button>
              <b-popover :target="`preset-info-shift-${si}`" triggers="hover focus" placement="left">
                <template #title>Atajos de horario</template>
                Selecciona un atajo para rellenar rápidamente el rango de días. Las horas se toman del primer día activo y se aplican a todos los días del rango. Los días fuera del rango quedan desactivados automáticamente. Puedes editar cada día de forma independiente después.
              </b-popover>
            </div>

            <div class="table-responsive">
              <table class="table table-sm schedule-table">
                <thead>
                  <tr>
                    <th>Día</th>
                    <th class="text-center">Activo</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th class="text-center">Descanso (min)</th>
                    <template v-if="shift.type === 'partida'">
                      <th>Entrada 2</th>
                      <th>Salida 2</th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in shift.days" :key="day.day" :class="{ 'day-row--off': !day.enabled }">
                    <td class="font-weight-bold" style="white-space:nowrap;">{{ day.label }}</td>
                    <td class="text-center">
                      <b-form-checkbox v-model="day.enabled" switch />
                    </td>
                    <td>
                      <small class="col-label">Entrada</small>
                      <div class="time-input-wrap">
                        <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <b-form-input v-model="day.start_time" type="time" :disabled="!day.enabled" :state="fieldError(si + '-' + day.day + '-start_time')" @focus="clearFieldError(si + '-' + day.day + '-start_time')" size="sm" />
                      </div>
                    </td>
                    <td>
                      <small class="col-label">Salida</small>
                      <div class="time-input-wrap">
                        <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <b-form-input v-model="day.end_time" type="time" :disabled="!day.enabled" :state="fieldError(si + '-' + day.day + '-end_time')" @focus="clearFieldError(si + '-' + day.day + '-end_time')" size="sm" />
                      </div>
                    </td>
                    <td>
                      <small class="col-label">Descanso</small>
                      <div class="d-flex justify-content-center">
                        <b-form-input v-model.number="day.rest_minutes" type="number" min="0" :disabled="!day.enabled" size="sm" style="width:70px;" />
                      </div>
                    </td>
                    <template v-if="shift.type === 'partida'">
                      <td>
                        <small class="col-label">Entrada 2</small>
                        <div class="time-input-wrap">
                          <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          <b-form-input v-model="day.start_time2" type="time" :disabled="!day.enabled" :state="fieldError(si + '-' + day.day + '-start_time2')" @focus="clearFieldError(si + '-' + day.day + '-start_time2')" size="sm" />
                        </div>
                      </td>
                      <td>
                        <small class="col-label">Salida 2</small>
                        <div class="time-input-wrap">
                          <svg class="time-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          <b-form-input v-model="day.end_time2" type="time" :disabled="!day.enabled" :state="fieldError(si + '-' + day.day + '-end_time2')" @focus="clearFieldError(si + '-' + day.day + '-end_time2')" size="sm" />
                        </div>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <b-button
            v-if="form.schedule.shifts.length < 3"
            variant="outline-primary"
            size="sm"
            @click="addShift"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            Añadir turno
          </b-button>
        </div>
      </b-card>

      <!-- ── STEP 4: Resumen ──────────────────────────────────── -->
      <b-card v-if="currentStep === 3" class="wizard-card">
        <h4 class="mb-25 font-weight-bold">Resumen</h4>
        <p class="text-muted mb-2">Revisa los datos antes de crear tu cuenta demo (válida 30 días).</p>

        <b-list-group flush class="mb-2">
          <b-list-group-item class="px-0">
            <div class="text-muted" style="font-size:0.8rem;">EMPRESA</div>
            <strong>{{ form.company.name }}</strong>
            <span class="text-muted ml-1">· {{ form.company.autonomous_community }}</span>
          </b-list-group-item>
          <b-list-group-item class="px-0">
            <div class="text-muted" style="font-size:0.8rem;">ADMINISTRADOR</div>
            <strong>{{ form.user.name }} {{ form.user.surname }}</strong>
            <span class="text-muted ml-1">· {{ form.user.email }}</span>
          </b-list-group-item>
          <b-list-group-item class="px-0">
            <div class="text-muted" style="font-size:0.8rem;">HORARIO</div>
            <span v-if="!form.schedule.has_shifts">
              Jornada <strong>{{ form.schedule.type }}</strong> ·
              {{ activeDaysCount(form.schedule.days) }} días activos
            </span>
            <span v-else>
              <strong>{{ form.schedule.shifts.length }} turno(s)</strong>:
              {{ form.schedule.shifts.map(s => s.name || 'Sin nombre').join(', ') }}
            </span>
          </b-list-group-item>
        </b-list-group>

        <!-- Server error messages -->
        <b-alert v-if="serverError" show variant="danger" class="mb-2">
          <div v-if="typeof serverError === 'string'">{{ serverError }}</div>
          <ul v-else class="mb-0 pl-1">
            <li v-for="(msgs, field) in serverError" :key="field">
              <strong>{{ field }}:</strong> {{ Array.isArray(msgs) ? msgs.join(', ') : msgs }}
            </li>
          </ul>
        </b-alert>

        <p class="text-muted" style="font-size:0.8rem;">
          Al registrarte, se crearán automáticamente 5 operarios ficticios con datos de ejemplo.
        </p>
      </b-card>

      <!-- Confirm modal -->
      <b-modal
        v-model="showConfirmModal"
        centered
        hide-header
        hide-footer
        no-close-on-backdrop
        size="sm"
      >
        <div class="text-center py-1">
          <div class="confirm-modal-icon mb-2">
            <feather-icon icon="CheckCircleIcon" size="36" class="text-primary" />
          </div>
          <h4 class="font-weight-bold mb-1">¿Listo para empezar?</h4>
          <p class="text-muted mb-2" style="font-size:0.9rem;">
            Se creará tu cuenta demo con una validez de <strong>30 días</strong>
            e incluirá <strong>5 empleados de ejemplo</strong> para que puedas explorar todas las funcionalidades.
          </p>
          <div class="d-flex justify-content-center" style="gap:12px;">
            <button class="btn btn-back" :disabled="submitting" @click="showConfirmModal = false">
              Volver a revisar
            </button>
            <b-button variant="primary" :disabled="submitting" @click="confirmAndSubmit">
              <b-spinner v-if="submitting" small class="mr-50" />
              Sí, crear mi cuenta
            </b-button>
          </div>
        </div>
      </b-modal>

      <!-- Navigation -->
      <div class="wizard-nav d-flex justify-content-between mt-2">
        <button
          v-if="currentStep > 0"
          class="btn btn-back"
          :disabled="submitting"
          @click="prevStep"
        >
          <feather-icon icon="ChevronLeftIcon" size="14" class="mr-50" />
          Anterior
        </button>
        <div v-else />

        <b-button
          v-if="currentStep < steps.length - 1"
          key="btn-next"
          variant="primary"
          @click="nextStep"
        >
          Siguiente
          <feather-icon icon="ChevronRightIcon" size="14" class="ml-50" />
        </b-button>
        <b-button
          v-else
          key="btn-submit"
          variant="primary"
          @click="submitForm"
          :disabled="submitting"
        >
          <b-spinner v-if="submitting" small class="mr-50" />
          Crear cuenta demo
        </b-button>
      </div>

      <!-- Login link -->
      <div class="text-center mt-2">
        <small class="text-muted">
          ¿Ya tienes cuenta?
          <b-link :to="{ name: 'login' }" class="text-primary font-weight-bold">Inicia sesión</b-link>
        </small>
      </div>

    </div>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BFormRadioGroup,
  BButton, BRow, BCol, BAlert, BListGroup, BListGroupItem, BLink,
  BInputGroup, BInputGroupAppend, BImg, BSpinner, BPopover, BModal,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email, min, confirmed } from '@validations'
import { mapActions } from 'vuex'
import { $themeConfig } from '../../themeConfig'

const DAYS_TEMPLATE = [
  { day: 'monday',    label: 'Lunes',      enabled: true,  start_time: '09:00', end_time: '17:00', rest_minutes: 30, start_time2: null, end_time2: null },
  { day: 'tuesday',   label: 'Martes',     enabled: true,  start_time: '09:00', end_time: '17:00', rest_minutes: 30, start_time2: null, end_time2: null },
  { day: 'wednesday', label: 'Miércoles',  enabled: true,  start_time: '09:00', end_time: '17:00', rest_minutes: 30, start_time2: null, end_time2: null },
  { day: 'thursday',  label: 'Jueves',     enabled: true,  start_time: '09:00', end_time: '17:00', rest_minutes: 30, start_time2: null, end_time2: null },
  { day: 'friday',    label: 'Viernes',    enabled: true,  start_time: '09:00', end_time: '17:00', rest_minutes: 30, start_time2: null, end_time2: null },
  { day: 'saturday',  label: 'Sábado',     enabled: false, start_time: null,    end_time: null,    rest_minutes: 0,  is_night_shift: false, start_time2: null, end_time2: null },
  { day: 'sunday',    label: 'Domingo',    enabled: false, start_time: null,    end_time: null,    rest_minutes: 0,  is_night_shift: false, start_time2: null, end_time2: null },
]

const AUTONOMOUS_COMMUNITIES = [
  'Andalucía', 'Aragón', 'Principado de Asturias', 'Illes Balears', 'Canarias',
  'Cantabria', 'Castilla-La Mancha', 'Castilla y León', 'Cataluña',
  'Comunitat Valenciana', 'Extremadura', 'Galicia', 'Comunidad de Madrid',
  'Región de Murcia', 'Comunidad Foral de Navarra', 'País Vasco', 'La Rioja',
  'Ceuta', 'Melilla',
]

function makeDefaultDays() {
  return DAYS_TEMPLATE.map(d => ({ ...d }))
}

function makeDefaultShift(name = '') {
  return {
    name,
    type: 'continua',
    is_night_shift: false,
    days: makeDefaultDays(),
  }
}

const DRAFT_KEY = 'onboarding_draft'

export default {
  name: 'OnboardingWizard',
  components: {
    BCard, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BFormRadioGroup,
    BButton, BRow, BCol, BAlert, BListGroup, BListGroupItem, BLink,
    BInputGroup, BInputGroupAppend, BImg, BSpinner, BPopover, BModal,
    ValidationProvider, ValidationObserver,
  },
  data() {
    const saved = this.loadDraft()
    return {
      currentStep: 0,
      submitting: false,
      serverError: null,
      scheduleError: null,
      scheduleFieldErrors: {},
      showPassword: false,
      showPasswordConfirm: false,
      showConfirmModal: false,
      required,
      email,
      min,
      confirmed,
      steps: [
        { title: 'Empresa' },
        { title: 'Cuenta' },
        { title: 'Horario' },
        { title: 'Resumen' },
      ],
      dayPresets: [
        { key: 'lv',  label: 'L – V' },
        { key: 'ls',  label: 'L – S' },
        { key: 'all', label: 'Todos' },
      ],
      form: saved || {
        company: {
          name: '',
          autonomous_community: '',
        },
        user: {
          name: '',
          surname: '',
          email: '',
          password: '',
          passwordConfirm: '',
        },
        schedule: {
          has_shifts: false,
          is_night_shift: false,
          type: 'continua',
          days: makeDefaultDays(),
          shifts: [makeDefaultShift('Turno Mañana')],
        },
      },
    }
  },
  computed: {
    autonomousCommunityOptions() {
      return [
        { value: '', text: 'Selecciona una comunidad...', disabled: true },
        ...AUTONOMOUS_COMMUNITIES.map(c => ({ value: c, text: c })),
      ]
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.saveDraft()
      },
    },
    currentStep(newVal) {
      this.$nextTick(() => {
        const refName = `step${newVal + 1}`
        if (this.$refs[refName]) {
          this.$refs[refName].reset()
        }
      })
    },
    'form.schedule.has_shifts'(newVal) {
      if (!newVal) {
        // Al desactivar turnos: el horario simple toma los valores del turno 1
        const s0 = this.form.schedule.shifts[0]
        this.form.schedule.type = s0.type
        this.form.schedule.days = s0.days.map(d => ({ ...d }))
      } else {
        // Al activar turnos: el turno 1 toma los valores del horario simple
        this.form.schedule.shifts[0].type = this.form.schedule.type
        this.form.schedule.shifts[0].days = this.form.schedule.days.map(d => ({ ...d }))
      }
    },
  },
  methods: {
    ...mapActions({ registerDemo: 'auth/registerDemo' }),

    loadDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY)
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    },

    saveDraft() {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(this.form))
      } catch {
        // ignore storage errors
      }
    },

    async nextStep() {
      this.scheduleError = null
      const refName = `step${this.currentStep + 1}`
      const observer = this.$refs[refName]
      if (observer) {
        const valid = await observer.validate()
        if (!valid) return
      }
      if (this.currentStep === 2 && !this.validateSchedule()) return
      this.currentStep++
    },

    prevStep() {
      this.serverError = null
      this.scheduleError = null
      this.scheduleFieldErrors = {}
      this.currentStep--
    },

    validateSchedule() {
      this.scheduleFieldErrors = {}
      if (!this.form.schedule.has_shifts) {
        const hasActive = this.form.schedule.days.some(d => d.enabled)
        if (!hasActive) {
          this.scheduleError = 'Debes activar al menos un día de trabajo.'
          return false
        }
        const hoursError = this.validateDayHours(this.form.schedule.days, this.form.schedule.type, '', '')
        if (hoursError) {
          this.scheduleError = hoursError
          return false
        }
      } else {
        if (!this.form.schedule.shifts.length) {
          this.scheduleError = 'Debes añadir al menos un turno.'
          return false
        }
        for (let i = 0; i < this.form.schedule.shifts.length; i++) {
          const shift = this.form.schedule.shifts[i]
          if (!shift.name.trim()) {
            this.scheduleError = `El turno ${i + 1} debe tener un nombre.`
            return false
          }
          const hasActive = shift.days.some(d => d.enabled)
          if (!hasActive) {
            this.scheduleError = `El turno "${shift.name}" debe tener al menos un día activo.`
            return false
          }
        }
        let hoursError = null
        for (let i = 0; i < this.form.schedule.shifts.length; i++) {
          const shift = this.form.schedule.shifts[i]
          const err = this.validateDayHours(shift.days, shift.type, ` en el turno "${shift.name}"`, `${i}-`)
          if (err && !hoursError) hoursError = err
        }
        if (hoursError) {
          this.scheduleError = hoursError
          return false
        }
      }
      return true
    },

    validateDayHours(days, type, context, prefix) {
      let firstError = null
      for (const day of days) {
        if (!day.enabled) continue
        if (!day.start_time) {
          this.$set(this.scheduleFieldErrors, `${prefix}${day.day}-start_time`, true)
          if (!firstError) firstError = `El día "${day.label}"${context} debe tener hora de entrada y salida.`
        }
        if (!day.end_time) {
          this.$set(this.scheduleFieldErrors, `${prefix}${day.day}-end_time`, true)
          if (!firstError) firstError = `El día "${day.label}"${context} debe tener hora de entrada y salida.`
        }
        if (type === 'partida') {
          if (!day.start_time2) {
            this.$set(this.scheduleFieldErrors, `${prefix}${day.day}-start_time2`, true)
            if (!firstError) firstError = `El día "${day.label}"${context} debe tener la segunda entrada y salida (jornada partida).`
          }
          if (!day.end_time2) {
            this.$set(this.scheduleFieldErrors, `${prefix}${day.day}-end_time2`, true)
            if (!firstError) firstError = `El día "${day.label}"${context} debe tener la segunda entrada y salida (jornada partida).`
          }
        }
      }
      return firstError
    },

    fieldError(key) {
      return this.scheduleFieldErrors[key] ? false : null
    },

    clearFieldError(key) {
      this.$delete(this.scheduleFieldErrors, key)
    },

    addShift() {
      if (this.form.schedule.shifts.length < 3) {
        const names = ['Turno Mañana', 'Turno Tarde', 'Turno Noche']
        const name = names[this.form.schedule.shifts.length] || `Turno ${this.form.schedule.shifts.length + 1}`
        this.form.schedule.shifts.push(makeDefaultShift(name))
      }
    },

    removeShift(index) {
      this.form.schedule.shifts.splice(index, 1)
    },

    applyDayPreset(days, preset) {
      const source = days.find(d => d.enabled) || days[0]
      const ranges = { lv: [0, 1, 2, 3, 4], ls: [0, 1, 2, 3, 4, 5], all: [0, 1, 2, 3, 4, 5, 6] }
      const active = new Set(ranges[preset])
      days.forEach((day, i) => {
        if (active.has(i)) {
          day.enabled = true
          day.start_time = source.start_time || '09:00'
          day.end_time = source.end_time || '17:00'
          day.rest_minutes = source.rest_minutes ?? 30
          day.start_time2 = source.start_time2 || null
          day.end_time2 = source.end_time2 || null
        } else {
          day.enabled = false
        }
      })
    },

    activeDaysCount(days) {
      return days.filter(d => d.enabled).length
    },

    buildDayPayload(day, type) {
      if (!day.enabled) {
        return { day: day.day, enabled: false, start_time: null, end_time: null, rest_minutes: 0 }
      }
      const payload = {
        day: day.day,
        enabled: true,
        start_time: day.start_time || null,
        end_time: day.end_time || null,
        rest_minutes: day.rest_minutes || 0,
      }
      if (type === 'partida') {
        payload.start_time2 = day.start_time2 || null
        payload.end_time2 = day.end_time2 || null
      }
      return payload
    },

    buildPayload() {
      const { company, user, schedule } = this.form
      let schedulePayload

      if (!schedule.has_shifts) {
        schedulePayload = {
          has_shifts: false,
          type: schedule.type,
          is_night_shift: schedule.is_night_shift,
          days: schedule.days.map(d => this.buildDayPayload(d, schedule.type)),
        }
      } else {
        schedulePayload = {
          has_shifts: true,
          shifts: schedule.shifts.map(shift => ({
            name: shift.name,
            type: shift.type,
            is_night_shift: shift.is_night_shift,
            days: shift.days.map(d => this.buildDayPayload(d, shift.type)),
          })),
        }
      }

      return {
        user: {
          name: user.name,
          surname: user.surname,
          email: user.email,
          password: user.password,
        },
        company: {
          name: company.name,
          autonomous_community: company.autonomous_community,
        },
        schedule: schedulePayload,
      }
    },

    submitForm() {
      this.serverError = null
      this.showConfirmModal = true
    },

    async confirmAndSubmit() {
      this.submitting = true
      const payload = this.buildPayload()

      try {
        await this.registerDemo(payload)
      } catch (error) {
        this.submitting = false
        this.showConfirmModal = false
        const response = error.response
        if (!response) {
          this.serverError = 'Error de conexión. Comprueba tu conexión a internet.'
          return
        }
        if (response.status === 422) {
          this.serverError = response.data.errors || response.data.message || 'Datos no válidos.'
        } else if (response.status === 429) {
          this.serverError = response.data.message || 'Demasiados intentos. Inténtalo de nuevo en unos minutos.'
        } else {
          this.serverError = response.data.message || 'Ha ocurrido un error inesperado.'
        }
      }
    },
  },
  setup() {
    const { appLogoImage } = $themeConfig.app
    return { appLogoImage }
  },
}
</script>

<style lang="scss" scoped>
.onboarding-page {
  min-height: 100vh;
  background: #f6f6f6;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;

  @media (max-width: 575px) {
    padding: 1rem 0.75rem;
  }
}

.onboarding-container {
  width: 100%;
  max-width: 900px;
}

.onboarding-logo {
  max-height: 48px;
  width: auto;
}

.wizard-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
}

// Step indicator
.wizard-steps {
  gap: 0;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  background: #e0e0e0;
  color: #666;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;

  &.step-active {
    background: #f1580c;
    color: #fff;
  }

  &.step-done {
    background: #28c76f;
    color: #fff;
  }
}

.step-line {
  width: 48px;
  height: 3px;
  background: #e0e0e0;
  flex-shrink: 0;
  transition: background 0.2s;

  @media (max-width: 575px) {
    width: 28px;
  }

  &.step-line-done {
    background: #28c76f;
  }
}

// Schedule table
.schedule-table {
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    padding: 0.4rem 0.5rem;
  }

  td {
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
  }

  @media (max-width: 767px) {
    // Reflow table as CSS grid — one row per day, 2 visual rows
    display: block;

    thead { display: none; }
    tbody { display: block; }

    tr {
      display: grid;
      grid-template-columns: 1fr 1fr 76px;
      grid-template-rows: auto auto;
      padding: 0.55rem 0;
      border-bottom: 1px solid #ebe9f1;

      td { border: none; padding: 0.2rem 0.25rem; }

      // Fila 1: nombre del día (cols 1-2) + toggle (col 3)
      td:nth-child(1) {
        grid-column: 1 / 3;
        grid-row: 1;
        display: flex;
        align-items: center;
        padding-left: 0;
        white-space: nowrap;
      }
      td:nth-child(2) {
        grid-column: 3;
        grid-row: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-right: 0;
      }

      // Fila 2: Entrada | Salida | Descanso
      td:nth-child(3) { grid-column: 1; grid-row: 2; padding-left: 0; }
      td:nth-child(4) { grid-column: 2; grid-row: 2; }
      td:nth-child(5) { grid-column: 3; grid-row: 2; padding-right: 0; }

      // Fila 3 (solo jornada partida): Entrada 2 | Salida 2
      td:nth-child(6) { grid-column: 1; grid-row: 3; padding-left: 0; }
      td:nth-child(7) { grid-column: 2; grid-row: 3; }

      // Switch: no estirarlo, que mantenga su tamaño natural
      td:nth-child(2) .custom-control {
        width: auto;
        flex-shrink: 0;
      }

      // Input tds: label encima + input debajo
      td:nth-child(n+3) {
        display: flex;
        flex-direction: column;

        .time-input-wrap {
          display: flex; // evita inline-flex que genera altura diferente
          min-width: 0;
          width: 100%;
        }

        .form-control {
          min-width: 0;
          width: 100%;
          height: 31px; // iguala alturas entre time inputs y number input
        }

        .form-control[type="number"] {
          text-align: center;
          padding-left: 0.3rem;
          padding-right: 0.3rem;
        }
      }
    }

    // Solo opacar los inputs, no el nombre ni el toggle
    .day-row--off td:nth-child(n+3) { opacity: 0.45; }
  }
}

.col-label {
  display: none;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
  font-weight: 600;
  margin-bottom: 0.2rem;
  white-space: nowrap;

  @media (max-width: 767px) {
    display: block;
  }
}

.time-input-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 108px;
  width: 100%;

  .time-icon {
    position: absolute;
    left: 0.55rem;
    color: #b9b9c3;
    pointer-events: none;
    flex-shrink: 0;
    z-index: 2;
  }

  .form-control {
    padding-left: 1.85rem;
    padding-right: 0.4rem;
    -webkit-appearance: none;
    appearance: none;
    // Fix Safari: time value not vertically centered
    line-height: 1.5;
    height: 31px;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }

    &::-webkit-date-and-time-value {
      margin: 0;
      text-align: left;
    }
  }
}

.shift-block {
  background: #fafafa;
}

// Confirm modal
.confirm-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(241, 88, 12, 0.1);
}

// Day preset pills
.preset-info-btn {
  background: transparent !important;
  border: none !important;
  color: #6e6b7b !important;
  box-shadow: none !important;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    color: #111111 !important;
    box-shadow: none !important;
  }
}

.preset-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.preset-label {
  color: #5e5873;
  font-weight: 600;
}

.preset-pill {
  font-size: 0.75rem;
  padding: 0.15rem 0.65rem;
  line-height: 1.4;
  background: transparent !important;
  border: 1px solid #6e6b7b !important;
  color: #6e6b7b !important;
  text-decoration: none !important;

  &:hover,
  &:focus,
  &:active,
  &:not(:disabled):not(.disabled):active {
    background: #6e6b7b !important;
    border-color: #6e6b7b !important;
    color: #ffffff !important;
    box-shadow: none !important;
  }
}

.wizard-nav {
  @media (max-width: 575px) {
    gap: 0.5rem;
  }
}

// Back button
.wizard-nav .btn {
  font-size: 0.8rem;
}

.btn-back {
  background: transparent !important;
  border: 1px solid #111111 !important;
  color: #111111 !important;
  text-decoration: none !important;

  &:hover,
  &:focus,
  &:active,
  &:not(:disabled):not(.disabled):active {
    background: #111111 !important;
    border-color: #111111 !important;
    color: #ffffff !important;
    box-shadow: none !important;
  }
}
</style>

